import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { DatesService } from 'app/infrastructure/services/alpura/dates.service';
import Swal from 'sweetalert2';
import { environment } from 'environments/environment';
import { CalculaLiquidacionService } from '@infrastructure/pages/reportes/calcula-liquidacion.service';
import { lastValueFrom } from 'rxjs';

/** Configuración de cada archivo que se descarga al generar la transferencia. */
interface ArchivoDescarga {
  /** Sufijo numérico del endpoint (transferenciaUniond + sufijo). */
  endpointSufijo: string;
  /** Prefijo del nombre de archivo a descargar. */
  nombre: string;
}

@Component({
  selector: 'app-transferencia-union-credito',
  templateUrl: './transferencia-union-credito.component.html',
  styleUrls: ['./transferencia-union-credito.component.scss'],
  providers: [DatesService, CalculaLiquidacionService],
})
export class TransferenciaUnionCreditoComponent implements OnInit {
  constructor(
    private _datesServ: DatesService,
    private _cl: CalculaLiquidacionService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.fillYear();
  }

  // ==========================================================
  // Estado / variables del componente
  // ==========================================================
  public anio: any = ' ';
  public semana: any = '';
  public fechaInicio = '';
  public fechaFinal = '';
  public archivos_gen = '';

  ListaSemanas: string[] = [];
  ListaAnios: any[] = [];
  datosArchivos: string[] = [];

  inicio: any = '';
  final: any = '';
  public aux: number = 0;

  /** Archivos que se generan/descargan al enviar el formulario. */
  private readonly ARCHIVOS_A_DESCARGAR: ArchivoDescarga[] = [
    { endpointSufijo: '1', nombre: 'PAGOLQLsem' },
    { endpointSufijo: '2', nombre: 'DEVSEMsem' },
    { endpointSufijo: '3', nombre: 'CATCONCsem' },
    { endpointSufijo: '4', nombre: 'PAGOSOCsem' },
  ];

  // ==========================================================
  // Formulario
  // ==========================================================
  form_data_union = new FormGroup({
    ejercicio: new FormControl('', Validators.required),
    semana: new FormControl('', Validators.required),
    fechaIni: new FormControl('', Validators.required),
    fechaFin: new FormControl('', Validators.required),
    tipoLiquidacion: new FormControl('previa', Validators.required),
  });

  // ==========================================================
  // Eventos del formulario
  // ==========================================================

  /** Se dispara al cambiar el año: recalcula la lista de semanas disponibles. */
  selectYear(): void {
    this.anio = this.form_data_union.controls['ejercicio'].value;
    this.fillWeeks();
  }

  /** Se dispara al cambiar la semana: obtiene el rango de fechas (inicio/fin). */
  selectWeek(): void {
    this.semana = this.form_data_union.controls['semana'].value;
    const datos_fecha = this.anio + '/' + this.semana;
    const url = environment.getDataFromDate + datos_fecha;

    this.http.get(url).subscribe((response: any) => {
      const fechaDelDate = response.startDate;
      const fechaAlDate = response.endDate;

      this.inicio = fechaDelDate.substring(0, 10);
      this.final = fechaAlDate.substring(0, 10);
    });
  }

  /** Construye el payload a partir del formulario (fechas sin guiones). */
  private buildPayload(): any {
    const data: any = {};
    data['ejercicio'] = this.form_data_union.value.ejercicio;
    data['semana'] = this.form_data_union.value.semana;
    data['fechaIni'] = this.form_data_union.value.fechaIni?.replace(
      /(-)/gm,
      '',
    );
    data['fechaFin'] = this.form_data_union.value.fechaFin?.replace(
      /(-)/gm,
      '',
    );
    data['tipoLiquidacion'] = this.form_data_union.value.tipoLiquidacion;
    return data;
  }

  /** Genera los archivos de la transferencia (llama al backend y descarga cada archivo). */
  async onSendData() {
    const data = this.buildPayload();
    const validate = this.validate();
    const semanaCerrada = await this.validarSemanaCerrada(
      data.ejercicio,
      data.semana,
    );
    

    if (validate !== 0) {
      this.mostrarErrorValidacion(validate);
      return;
    }
    if (semanaCerrada && data.tipoLiquidacion === 'previa') {
      Swal.fire({
        icon: 'error',
        title: '¡Error!',
        html: `<b>La semana ${data.semana} del año ${data.ejercicio} ya se encuentra cerrada.`,
      });
      return;
    } else if (!semanaCerrada && data.tipoLiquidacion === 'definitiva') {
      Swal.fire({
        icon: 'error',
        title: '¡Error!',
        html: `<b>La semana ${data.semana} del año ${data.ejercicio} no es definitiva.`,
      });
      return;
    }

    const url = environment.transferenciaUnion;
    this.http.post(url, data).subscribe((response: any) => {
      this.descargarArchivos(data);

      if (response.code == 200) {
        Swal.fire({
          icon: 'success',
          title: 'Archivos Generados',
          showConfirmButton: true,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Archivos Sin Información',
          showConfirmButton: true,
        });
      }
    });
  }

  /** Descarga cada uno de los archivos configurados en ARCHIVOS_A_DESCARGAR. */
  private descargarArchivos(data: any): void {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    this.ARCHIVOS_A_DESCARGAR.forEach((archivo) => {
      const url = environment.transferenciaUniond + archivo.endpointSufijo;
      this.http
        .post(url, data, { headers, responseType: 'arraybuffer' })
        .subscribe((response: any) => {
          this.handleFileDownload(
            response,
            archivo.nombre,
            data['ejercicio'],
            data['semana'],
          );
        });
    });
  }

  /** Limpia el formulario y las fechas mostradas. */
  clean(): void {
    this.final = '';
    this.inicio = '';
    this.form_data_union.controls['ejercicio'].setValue('');
    this.form_data_union.controls['semana'].setValue('');
    this.form_data_union.controls['fechaIni'].setValue('');
    this.form_data_union.controls['fechaFin'].setValue('');
  }

  /** Envía los archivos ya generados al destino final. */
  transfer(): void {
    const validate = this.validate();
    if (validate !== 0) {
      this.mostrarErrorValidacion(validate);
      return;
    }

    const data = this.buildPayload();
    const url = environment.transferenciaUnionArchivos;

    this.http.post(url, data).subscribe((response: any) => {
      if (response.code == 200) {
        Swal.fire({
          icon: 'success',
          title: 'Archivos Enviados',
          showConfirmButton: true,
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Archivos no transferidos',
          showConfirmButton: true,
        });
      }
    });
  }

  // ==========================================================
  // Funciones auxiliares
  // ==========================================================

  async validarSemanaCerrada(year: number, week: number, facility = 1) {
    var body = {
      facility: facility,
      year: year,
      week: week,
    };

    const data$ = this._cl.getInfoCierreSemanalFac(body);
    const data: any = await lastValueFrom(data$);
    return data.validacion !== 'A';
  }

  /** Carga la lista de años disponibles para el select "Ejercicio". */
  fillYear(): void {
    this.ListaAnios = this._datesServ.getYears();
  }

  /** Carga la lista de semanas disponibles según el año seleccionado. */
  fillWeeks(): void {
    this.ListaSemanas.length = 0;
    const anio = this.anio;

    if (!anio) {
      return;
    }

    this._datesServ.getWeeksByYear(anio).subscribe(
      (semanas: any) => {
        for (let i = 1; i <= semanas; i++) {
          this.ListaSemanas.push(i.toString());
        }
      },
      () => {
        Swal.fire({
          icon: 'error',
          title: 'Ocurrió un error',
          html: `<b>ERR00</b><br><br><em class='fa fa-info-circle'></em>&nbsp; Al ejecutar el servicio`,
        });
      },
    );
  }

  /** Descarga un arraybuffer como archivo .txt con el nombre indicado. */
  handleFileDownload(
    data: any,
    nombre: string,
    ejercicio: string,
    semana: string,
  ): void {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${nombre}${semana}_${ejercicio}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  /** Valida que año y semana estén seleccionados. Retorna 0 si es válido. */
  validate(): number {
    const year = this.form_data_union.value.ejercicio;
    const week = this.form_data_union.value.semana;

    if (year == '' && week == '') {
      return 3;
    }
    if (week == '') {
      return 2;
    }
    if (year == '') {
      return 1;
    }
    return 0;
  }

  /** Muestra el mensaje de advertencia correspondiente al código de validación. */
  private mostrarErrorValidacion(codigo: number): void {
    let message = '';
    switch (codigo) {
      case 1:
        message = 'Seleccione un año para continuar';
        break;
      case 2:
        message = 'Seleccione una semana para continuar';
        break;
      case 3:
        message = 'Seleccione año y semana para continuar';
        break;
      default:
        break;
    }

    Swal.fire({
      icon: 'warning',
      title: 'Error',
      text: message,
      showConfirmButton: true,
    });
  }
}
